import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { contentGenerationAnalysisPages } from '../../../Types/enums';
import LearnMore from '../../LearnMore';

export type RegenerateDialogProps = {
  promptType: string;
  currentPrompt: string;
  handleSave: (text?: string) => void;
  handleClose: () => void;
};

const PromptTypeVariablesMap: Record<string, { [label: string]: string }> = {
  [contentGenerationAnalysisPages.SLIDE_DETAILS]: {
    Notes: 'Notes',
    'Rubric Summary': 'Rubric Summary',
    Rubric: 'Rubric',
  },
  [contentGenerationAnalysisPages.SLIDE_TITLE]: {
    Notes: 'Notes',
    'Slide Details': 'Slide Details\\\\.Slide Details',
  },
  [contentGenerationAnalysisPages.EXECUTIVE_SUMMARY]: {
    Notes: 'Notes',
    'Rubric Summary': 'Rubric Summary',
    'Slide Details': 'Slide Details\\\\.Slide Details',
    'Flash Report Key Questions': 'Flash Report\\\\.Key Questions',
  },
};

const RegenerateDialog = ({
  promptType,
  currentPrompt,
  handleSave,
  handleClose,
}: RegenerateDialogProps): JSX.Element => {
  const [currentText, setCurrentText] = useState(currentPrompt);
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [shouldRestoreCursor, setShouldRestoreCursor] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const variablesMap = useMemo(() => PromptTypeVariablesMap[promptType] || {}, [promptType]);

  const handleSelect = useCallback(() => {
    if (inputRef.current) {
      setCursorPos(inputRef.current.selectionStart);
    }
  }, []);

  const addVariable = (variable: string) => {
    const insertion = `\\\\$[${variablesMap[variable]}] `;
    const pos = cursorPos ?? currentText.length;
    const before = currentText.slice(0, pos);
    const after = currentText.slice(pos);
    const newText = before + insertion + after;
    setCurrentText(newText);
    // Update cursor position state so consecutive inserts go to the right place
    const newPos = pos + insertion.length;
    setCursorPos(newPos);
    setShouldRestoreCursor(true);
  };

  useLayoutEffect(() => {
    if (shouldRestoreCursor && cursorPos !== null && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
      inputRef.current.setSelectionRange(cursorPos, cursorPos);
      setShouldRestoreCursor(false);
    }
  }, [shouldRestoreCursor, cursorPos]);

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Regenerate</Typography>
            <Tooltip
              title="Regenerate the output by selecting the correct inputs (leave wildcards as added) and include instructions on how you would reprompt the output."
              placement="top"
            >
              <InfoOutlinedIcon fontSize="small" htmlColor="#070154" />
            </Tooltip>
            <LearnMore
              link="https://howto.westmonroe.com/a/1988270-analysis-in-intellio-advantage"
              iconSx={{ fontSize: '14px', ml: 1 }}
            />
          </Box>
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ paddingTop: '28px' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {Object.keys(variablesMap).map((label) => (
            <Chip
              key={label}
              label={label}
              clickable
              sx={{
                backgroundColor: '#E8EEF8',
                color: '#092B49',
                '&:hover': {
                  backgroundColor: '#D5DBE4',
                },
              }}
              onClick={() => addVariable(label)}
            />
          ))}
        </Box>
        <TextField
          id="expanded-chat-input"
          sx={{ backgroundColor: 'white' }}
          minRows={10}
          variant="outlined"
          placeholder="Please enter a prompt"
          autoComplete="off"
          autoFocus
          multiline
          fullWidth
          value={currentText}
          onChange={(e) => setCurrentText(e.target.value)}
          onSelect={handleSelect}
          inputRef={inputRef}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputProps: {
              style: { fontSize: '15px' },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '24px' }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => {
            handleSave(currentText !== '' ? currentText : undefined);
            handleClose();
          }}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegenerateDialog;
